'use client';

import { useEffect, useState, type ReactNode } from 'react';

const PROTOTYPE_MODE = process.env.NEXT_PUBLIC_PROTOTYPE_MODE === '1';

/**
 * When prototype mode is on, holds rendering until the MSW worker is
 * intercepting requests — it must wrap AuthProvider, whose mount-time
 * /api/auth/session fetch would otherwise race the worker start and hit the
 * real (absent) backend. When the flag is off this renders children directly
 * and the mock code is never loaded.
 */
export function PrototypeGate({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(!PROTOTYPE_MODE);
  const [ResetBadge, setResetBadge] = useState<React.ComponentType | null>(null);

  useEffect(() => {
    if (!PROTOTYPE_MODE) return;
    let cancelled = false;
    Promise.all([import('../msw/worker'), import('./ResetBadge')]).then(
      async ([workerMod, badgeMod]) => {
        await workerMod.startPrototypeWorker();
        if (!cancelled) {
          setResetBadge(() => badgeMod.ResetBadge);
          setReady(true);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) return null;
  return (
    <>
      {children}
      {ResetBadge ? <ResetBadge /> : null}
    </>
  );
}
